import { lazy } from 'react'

import { useRouteMatch } from 'react-router'

import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { secondaryCategoryReportConfig } from '@webapp/modules/reports/config/reportConfigs'
import { useReportSecondaryCategoryPresenter } from '@webapp/modules/reports/features/get-report-secondary-category/presenters/UseReportSecondaryCategoryPresenter'
import { useReportFilterCarryOver } from '@webapp/modules/reports/hooks/useReportFilterCarryOver'
import { type ReportDetailsPageParams } from '@webapp/modules/reports/pages/types'

const ReportDetailViewChunk = lazy(() =>
  import('@webapp/modules/reports/feature-sets/ReportDetailView').then(
    ({ ReportDetailView }) => ({
      default: ReportDetailView,
    })
  )
)

export const OrdersBySecondaryCategoryReportDetailsPage = () => {
  const intl = useProcurifyIntl()
  const pageTitle = intl.formatMessage(secondaryCategoryReportConfig.titleKey)
  const { url } = useAppRoutes()
  const { withReportFilters } = useReportFilterCarryOver()
  const match = useRouteMatch<ReportDetailsPageParams>()

  const entityId = Number(match.params.id)

  // Breadcrumb entity name resolved independently of the line-item fetch.
  // Same query key and staleTime as the Feature Set's header
  // hook, so its later mount reads the cached entity instead of refetching.
  // An unknown id ends the trail in the not-found copy the FS shows. Until
  // the name resolves, the report title is the only crumb.
  const {
    data: { secondaryCategoryName },
    states: { isNotFound },
  } = useReportSecondaryCategoryPresenter({
    secondaryCategoryId: entityId,
  })

  const entityCrumbName = isNotFound
    ? intl.formatMessage(secondaryCategoryReportConfig.detailNotFoundHeaderKey)
    : secondaryCategoryName

  return (
    <Page
      pageTitle={pageTitle}
      breadcrumbs={
        entityCrumbName
          ? [
              {
                link: withReportFilters(
                  url('OrdersBySecondaryCategoryReportPage')
                ),
                name: pageTitle,
                escapeCaseFormatting: true,
              },
              { name: entityCrumbName, escapeCaseFormatting: true },
            ]
          : [{ name: pageTitle, escapeCaseFormatting: true }]
      }
    >
      <ReportDetailViewChunk
        reportConfig={secondaryCategoryReportConfig}
        entityId={entityId}
      />
    </Page>
  )
}
